// Checklist: studioaeye-backend/docs/tests-Backend/testingCheckList.md
// Features: M004-F0607, M004-F0608, M004-F0609
// Member-centric Access v1 primary surface (design doc M004-D013 §10).
// This component intentionally does NOT render areas, door bindings, priority,
// automatic matching, or group CRUD — that remains in AccessControlPanel.js,
// reachable only from the Advanced route (§10.6).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { accessControlApi } from '../../services/api';

const stringId = (value) => (value === undefined || value === null ? '' : String(value));
const array = (value) => (Array.isArray(value) ? value : []);

const unwrap = (response) => response?.data?.data ?? response?.data ?? response ?? {};

const normaliseAssignment = (assignment = {}) => ({
  groupUuid: stringId(assignment.groupUuid ?? assignment.group_uuid ?? assignment.groupId ?? assignment.group_id),
  rowVersion: Number(assignment.rowVersion ?? assignment.row_version ?? 0),
});

const normaliseMember = (member = {}) => ({
  id: stringId(member.userUuid ?? member.user_uuid ?? member.uuid ?? member.userId ?? member.user_id ?? member.id),
  displayName: String(member.displayName ?? member.display_name ?? member.name ?? member.email ?? 'Unnamed member'),
  email: String(member.email ?? member.primaryEmail ?? member.primary_email ?? ''),
  assignments: array(member.assignments ?? member.groupAssignments ?? member.group_assignments)
    .map(normaliseAssignment)
    .filter((assignment) => assignment.groupUuid),
});

const idempotencyKey = () => (
  globalThis.crypto?.randomUUID?.() || `web-access-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const errorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.error?.message
  || error?.message
  || fallback
);

const AccessMemberList = ({ organisationId }) => {
  const [generalAccessGroupUuid, setGeneralAccessGroupUuid] = useState('');
  // hasAnyDoorBinding starts undefined (not false) so a load that hasn't
  // resolved yet does not briefly claim doors are connected; the banner
  // renders whenever this is not strictly true, per §10.4.
  const [hasAnyDoorBinding, setHasAnyDoorBinding] = useState(undefined);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [togglingMemberId, setTogglingMemberId] = useState('');

  const loadOverview = useCallback(async ({ signal } = {}) => {
    if (!organisationId) return;
    setLoading(true);
    setError('');
    try {
      const response = await accessControlApi.getOverview(organisationId, { signal });
      const payload = unwrap(response);
      setGeneralAccessGroupUuid(stringId(payload.generalAccessGroupUuid ?? payload.general_access_group_uuid));
      // Fail toward showing the honesty banner: only "true" suppresses it.
      const doorBindingValue = payload.hasAnyDoorBinding ?? payload.has_any_door_binding;
      setHasAnyDoorBinding(doorBindingValue === true);
      setMembers(array(payload.members ?? payload.users).map(normaliseMember).filter((member) => member.id));
    } catch (loadError) {
      if (loadError?.code === 'ERR_CANCELED' || loadError?.name === 'CanceledError' || signal?.aborted) return;
      setError(errorMessage(loadError, 'Unable to load access settings.'));
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    setMembers([]);
    setError('');
    setNotice('');
    setSearch('');
    setHasAnyDoorBinding(undefined);
    setGeneralAccessGroupUuid('');
    if (!organisationId) return undefined;
    const controller = new AbortController();
    loadOverview({ signal: controller.signal });
    return () => controller.abort();
  }, [organisationId, loadOverview]);

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter((member) => (
      member.displayName.toLowerCase().includes(term) || member.email.toLowerCase().includes(term)
    ));
  }, [members, search]);

  const toggleAccess = async (member, nextOn) => {
    if (!organisationId || !generalAccessGroupUuid || togglingMemberId) return;
    setTogglingMemberId(member.id);
    setError('');
    setNotice('');
    const key = idempotencyKey();
    try {
      if (nextOn) {
        await accessControlApi.addManualGroupMember(organisationId, generalAccessGroupUuid, member.id, {}, key);
      } else {
        const assignment = member.assignments.find((candidate) => candidate.groupUuid === generalAccessGroupUuid);
        await accessControlApi.removeManualGroupMember(
          organisationId,
          generalAccessGroupUuid,
          member.id,
          { rowVersion: assignment?.rowVersion },
          key,
        );
      }
      await loadOverview();
      setNotice(nextOn ? `Access enabled for ${member.displayName}.` : `Access disabled for ${member.displayName}.`);
    } catch (toggleError) {
      setError(errorMessage(toggleError, 'Unable to update access.'));
    } finally {
      setTogglingMemberId('');
    }
  };

  if (!organisationId) {
    return (
      <section className="access-empty" data-testid="WEB-ACCESS-MEMBER-000">
        <h2>Choose one organisation</h2>
        <p>Access settings are tenant-specific. Select an organisation above before viewing or changing member access.</p>
      </section>
    );
  }

  if (loading && !members.length && hasAnyDoorBinding === undefined) {
    return <div className="loading-spinner" data-testid="WEB-ACCESS-MEMBER-LOADING">Loading access settings...</div>;
  }

  return (
    <section className="access-member-list" data-testid="WEB-ACCESS-MEMBER-001">
      {hasAnyDoorBinding !== true && (
        <div className="access-message warning" role="alert" data-testid="access-honesty-banner">
          No physical doors are connected for this organisation yet — access recorded here is not enforced at any door.
        </div>
      )}

      {error && <div className="access-message error" role="alert">{error}</div>}
      {notice && <div className="access-message success" role="status">{notice}</div>}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search members..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="search-input"
          data-testid="access-member-search"
          aria-label="Search members"
        />
      </div>

      <div className="access-member-table-wrap">
        <table className="access-member-table">
          <thead><tr><th>Member</th><th>Access</th></tr></thead>
          <tbody>
            {filteredMembers.map((member) => {
              const generalAccessOn = member.assignments.some((assignment) => assignment.groupUuid === generalAccessGroupUuid);
              const hasAnyAccess = member.assignments.length > 0;
              const mismatched = hasAnyAccess && !generalAccessOn;
              return (
                <tr key={member.id} data-testid={`access-member-row-${member.id}`}>
                  <td>
                    <strong>{member.displayName}</strong>
                    <span>{member.email}</span>
                  </td>
                  <td>
                    <label className="access-toggle">
                      <input
                        type="checkbox"
                        checked={generalAccessOn}
                        onChange={(event) => toggleAccess(member, event.target.checked)}
                        disabled={Boolean(togglingMemberId) || !generalAccessGroupUuid}
                        aria-label={`Toggle access for ${member.displayName}`}
                      />
                      <span className={hasAnyAccess ? 'access-state-on' : 'access-state-off'}>
                        {hasAnyAccess ? 'Has access' : 'No access'}
                      </span>
                    </label>
                    {mismatched && (
                      <small className="access-mismatch-note" title="Access granted via another group" data-testid={`access-mismatch-${member.id}`}>
                        Access granted via another group
                      </small>
                    )}
                  </td>
                </tr>
              );
            })}
            {!filteredMembers.length && (
              <tr><td colSpan={2}>No members match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default AccessMemberList;
